import { FC, useCallback, useEffect, useMemo } from 'react';
import { gql } from 'graphql-request';
import { PracticeAnalyticsAggregations, PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { AnalyticsHooks, TabbedTable } from '@frontend/analytics-shared';
import { processExportableData } from '@frontend/file-download-helper';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { formatters, useIsSmallMode } from '@frontend/shared';
import { TableColumnConfig, useAlert } from '@frontend/design-system';
import { useShowBulkMessageButton } from '../../../hooks/use-show-bulk-message-button';
import { queryKeys } from '../../../query-keys';
import { drillDownTableSize } from '../../../views/common-styles';
import { InlineStats } from '../../inline-stats';
import { BulkMessageButton } from '../bulk-message-button';
import { collectPatientIds, deriveExportValue } from '../helpers/patients';
import { getPracticeAnalyticsQueryVariables } from '../helpers/query-variables';
import { useSubViewTableColConfig } from '../helpers/subview-table-col-config';
import { usePatientsLastContacted } from '../hooks/use-patients-last-contacted';
import { usePracticeAnalyticsShallowStore } from '../hooks/use-practice-analytics-store';
import { RecapturedMetric } from '../recaptured-metric';
import { SmallModePatientsList } from '../small-mode-patients-list';

type Tab = 'all';

type LargeTableProps = Omit<PracticeAnalyticsTypes.LargeModeTableProps<Tab>, 'initialTab'>;

const query = gql`
  query ($start: Int!, $end: Int!, $step: TimeStep!) {
    location {
      recapturedPatients: scheduledPatients {
        details(start: $start, end: $end, step: $step) {
          patients {
            id
            productionAmount
            nextAppointmentDate
            nextAppointmentScheduledDate
            FirstName: firstName
            LastName: lastName
            PMID: pmid
            MobilePhone: mobilePhone
            HomePhone: homePhone
            WorkPhone: workPhone
            Email: email
            PreferredName: preferredName
            Birthdate: birthdate
            Gender: gender
          }
        }
        totals(start: $start, end: $end, step: $step) {
          leadTime
          patients
          production
        }
      }
    }
  }
`;

export const RecapturedPatientsSubView: FC<React.PropsWithChildren<PracticeAnalyticsTypes.MetricSubViewProps>> = ({
  exportEnabled,
  onPatientsLoad,
}) => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { isSmallMode } = useIsSmallMode();
  const { demoData, filters, isDemoAccount, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore(
    'demoData',
    'filters',
    'isDemoAccount',
    'showDemoChipAndBanner'
  );
  const { locationNames } = AnalyticsHooks.useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });
  const isBulkMessagingEnabled = useShowBulkMessageButton();

  const { data, isLoading } = useScopedQuery({
    queryKey: queryKeys.practiceAnalyticsCharts(
      `recapturedPatientsSubView-${isDemoAccount}-${JSON.stringify(filters)}`
    ),
    queryFn: () =>
      isDemoAccount || !filters.locations?.length
        ? null
        : PracticeAnalyticsApi.getPracticeAnalyticsRecords<PracticeAnalyticsTypes.RecapturedPatientsTrend>({
            locationIds: filters.locations,
            queries: [query],
            variables: getPracticeAnalyticsQueryVariables(filters),
          }),
    onError: () => {
      alert.error(t("Couldn't load the dashboard data. Please try again."));
    },
    select: (data) => (isDemoAccount ? demoData?.recapturedPatients : data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const processedData = useMemo(() => {
    return {
      aggregatedData: PracticeAnalyticsAggregations.recapturedPatientsDetails(data, filters.locations?.length),
      patientIds: Object.values(data?.data || {}).reduce((acc, locationDetails) => {
        return collectPatientIds(locationDetails?.location?.recapturedPatients.details?.patients, acc);
      }, [] as string[]),
    };
  }, [data, filters.locations?.length]);

  const { lastContactedDates, isLoading: isLoadingLastContacted } = usePatientsLastContacted({
    patientIds: processedData.patientIds,
  });

  useEffect(() => {
    onPatientsLoad?.(processedData.aggregatedData.recapturedPatients.details.patients || []);
  }, [onPatientsLoad, processedData]);

  const processExportableDataHandler = useCallback(
    (data: PracticeAnalyticsTypes.PatientInfo[]) =>
      // Translation is not needed as this is for export purpose
      processExportableData({
        columns: [
          'Patient Name',
          ...((filters.locations?.length || 0) > 1 ? ['Location Name'] : []),
          'Phone Number',
          'Email',
          'Recapture Date',
          'Last Contacted',
          '$ Value',
        ],
        data,
        deriveExportValue: (params) =>
          deriveExportValue({
            ...params,
            lastContactedDates,
            locationNames,
          }),
        sortColumn: 'productionAmount',
      }),
    [filters.locations, lastContactedDates, locationNames]
  );

  const tabs = useMemo(
    () => ({
      all: {
        data: processedData.aggregatedData.recapturedPatients.details.patients || [],
        label: t('All'),
      },
    }),
    [processedData.aggregatedData.recapturedPatients.details.patients, t]
  );

  const smallModeTableProps: PracticeAnalyticsTypes.SmallModePatientsListProps<Tab> = {
    exportFileName: 'Recaptured Patients', // Translation not needed
    initialTab: 'all',
    isBulkMessagingEnabled,
    isDemoAccount,
    isExportEnabled: exportEnabled,
    isLoadingLastContacted,
    isLoadingPatients: isLoading,
    processExportableData: processExportableDataHandler,
    tabs,
  };

  const largeModeTableProps: LargeTableProps = {
    ...smallModeTableProps,
    lastContactedDates,
    locationNames,
    multiLocations: (filters.locations?.length || 0) > 1,
  };

  return (
    <>
      <InlineStats
        data={[
          {
            key: 'recapturedPatients',
            name: t('Recaptured Patients'),
            value: formatters.value(processedData.aggregatedData.recapturedPatients.totals.patients || 0),
          },
          {
            key: 'recapturedProduction',
            name: t('Recaptured Production'),
            value: formatters.currency(processedData.aggregatedData.recapturedPatients.totals.production || 0),
          },
          isSmallMode
            ? null
            : {
                key: 'avgLeadTime',
                name: t('Avg. Lead Time (Days)'),
                value: formatters.value(
                  Math.round(processedData.aggregatedData.recapturedPatients.totals.leadTime || 0)
                ),
              },
        ]}
        isDemoAccount={showDemoChipAndBanner}
        isLoading={isLoading}
      />
      <RecapturedMetric clickNoop isDrillDownPage />
      {isSmallMode ? (
        <SmallModePatientsList<Tab> {...smallModeTableProps} trackingIdBase='recaptured-patients-small-mode' />
      ) : (
        <LargeModeTable {...largeModeTableProps} />
      )}
    </>
  );
};

const LargeModeTable = ({
  tabs,
  isBulkMessagingEnabled,
  isExportEnabled,
  isLoadingLastContacted,
  isLoadingPatients,
  processExportableData,
  ...rest
}: LargeTableProps) => {
  const { t } = useTranslation('analytics');

  const middleColumns: TableColumnConfig<PracticeAnalyticsTypes.PatientInfo>[] = [
    {
      Header: t('Recapture Date'),
      headerLabel: t('Recapture Date'),
      accessor: ({ nextAppointmentScheduledDate }) => formatters.date(nextAppointmentScheduledDate),
      id: 'nextAppointmentScheduledDate',
      sortType: 'date',
      sortInverted: true,
      width: 200,
    },
  ];

  return (
    <TabbedTable<PracticeAnalyticsTypes.PatientInfo>
      {...rest}
      actions={BulkMessageButton}
      autorizedToExport={PracticeAnalyticsApi.auditPracticeAnalyticsExport}
      colConfig={useSubViewTableColConfig({
        actionsTrackingIdBase: 'recaptured-patient',
        middleColumns,
        patientsLastContacted: {
          isLoading: isLoadingLastContacted,
          lastContactedDates: rest.lastContactedDates,
        },
      })}
      data={tabs.all.data || []}
      emptyStateConfig='users'
      isExportReady={!isLoadingPatients && !isLoadingLastContacted}
      isLoading={isLoadingPatients}
      isSelectable={isBulkMessagingEnabled}
      processExportableData={processExportableData}
      showExportIcon={isExportEnabled}
      tableInstanceId='recaptured-patients-details'
      trackingIdBase='recaptured-patients'
      wrapperStyle={drillDownTableSize}
    />
  );
};
